import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FilePen } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { CONCEPTOS, type Convenio } from './contrato';

export function ModalDeDatosDelDocumento({
  convenio: c,
  alCerrar,
}: {
  convenio: Convenio;
  alCerrar: () => void;
}) {
  const clienteDeConsultas = useQueryClient();
  const [numero, setNumero] = useState(c.numero ?? '');
  const [fecha, setFecha] = useState(c.fecha ?? '');
  const [representante, setRepresentante] = useState(c.representante ?? '');
  const [concepto, setConcepto] = useState(c.concepto ?? '');
  const [fechaSolicitud, setFechaSolicitud] = useState(c.fechaSolicitud ?? '');
  const [sumilla, setSumilla] = useState(c.sumilla ?? '');
  const hoy = new Date().toISOString().slice(0, 10);

  const guardar = useMutation({
    mutationFn: () =>
      api.put<Convenio>(`/convenios/${c.id}/datos-del-documento`, {
        numero,
        fecha: fecha || null,
        representante,
        concepto,
        fechaSolicitud: fechaSolicitud || null,
        sumilla,
      }),
    onSuccess: () => {
      notificar.exito('El documento ya sale con ellos', 'Datos guardados');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['convenios'] });
      alCerrar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO GUARDAR'),
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Datos del documento"
      subtitulo={c.cliente}
      icono={FilePen}
      cerrarAlPulsarFuera={false}
    >
      <div className="grid grid-cols-2 gap-3">
        {c.migrado && (
          <>
            <p className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[10px] uppercase leading-relaxed text-amber-800">
              Viene del libro de cartera, que no trae ni el número ni la fecha de firma. Cópielos
              del convenio en papel.
            </p>
            <Campo etiqueta="Número del convenio">
              <input
                value={numero}
                onChange={(e) => setNumero(e.target.value.toUpperCase())}
                placeholder="EPAGAL-CONV-TESO-0010-2026"
                className={`${CAMPO} importe uppercase`}
              />
            </Campo>
            <Campo etiqueta="Firmado el">
              <input
                type="date"
                value={fecha}
                max={hoy}
                onChange={(e) => setFecha(e.target.value)}
                className={CAMPO}
              />
            </Campo>
          </>
        )}
        <Campo etiqueta="Representante legal (si es sociedad)">
          <input
            value={representante}
            onChange={(e) => setRepresentante(e.target.value)}
            className={`${CAMPO} uppercase`}
          />
        </Campo>
        <Campo etiqueta="Fecha del oficio de solicitud">
          <input
            type="date"
            value={fechaSolicitud}
            max={hoy}
            onChange={(e) => setFechaSolicitud(e.target.value)}
            className={CAMPO}
          />
        </Campo>
        <div className="col-span-2">
          <Campo etiqueta="Por concepto de">
            <input
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder={CONCEPTOS[c.servicio]}
              className={CAMPO}
            />
          </Campo>
        </div>
        <div className="col-span-2">
          <Campo etiqueta="Sumilla de la gerencia en el oficio">
            <textarea
              value={sumilla}
              onChange={(e) => setSumilla(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-lg border border-armazon-200 px-2 py-1.5 text-xs"
            />
          </Campo>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={alCerrar}
          className="h-10 rounded-lg border border-armazon-200 bg-white px-4 text-xs font-bold uppercase text-armazon-600"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => guardar.mutate()}
          disabled={guardar.isPending}
          className="h-10 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white disabled:opacity-50"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

const CAMPO = 'h-9 w-full rounded-lg border border-armazon-200 px-2 text-xs';

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase text-armazon-500">{etiqueta}</span>
      {children}
    </label>
  );
}
